import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STAGES, OUTCOMES, inputCls, StageBadge, OutcomeBadge, Row } from "./_shared";

export default function PipelineAdmin({
  apps,
  appsSort,
  setAppsSort,
  onRefresh,
  selected,
  setSelected,
  updateApp,
  onPublishAsEpisode,
}) {
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    apps.forEach((a) => { if (g[a.status]) g[a.status].push(a); });
    return g;
  }, [apps]);

  return (
    <>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Pipeline</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter">{apps.length} applications</h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="apps-sort"
            value={appsSort}
            onChange={(e) => setAppsSort(e.target.value)}
            className={`h-10 px-3 ${inputCls}`}
          >
            <option value="queue">Sort: Queue #</option>
            <option value="applied_desc">Sort: Newest applied first</option>
            <option value="applied_asc">Sort: Oldest applied first</option>
          </select>
          <button onClick={onRefresh} className="px-4 py-2 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-lime hover:text-lime">Refresh</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kanban">
        {STAGES.map((s) => (
          <div key={s.key} data-testid={`kanban-${s.key}`} className="bg-surface border border-white/10 flex flex-col min-h-[300px]">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-widest text-white/70">{s.label}</span>
              <span className="text-xs font-mono text-lime tabular-nums">{grouped[s.key].length}</span>
            </div>
            <div className="p-3 space-y-3 flex-1">
              {grouped[s.key].map((a) => (
                <button
                  key={a.id}
                  data-testid={`app-card-${a.id}`}
                  onClick={() => setSelected(a)}
                  className="w-full text-left bg-black border border-white/10 p-3 hover:border-lime transition-colors"
                >
                  <div className="font-heading text-sm font-bold uppercase tracking-tight truncate">{a.startup_name}</div>
                  <div className="text-xs text-white/50 mt-1 line-clamp-2">{a.one_liner}</div>
                  <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/40">
                    <span>#{a.queue_position}</span>
                    <span>{a.country}</span>
                  </div>
                  <div data-testid={`app-card-applied-${a.id}`} className="mt-1 text-[9px] font-mono uppercase tracking-widest text-white/30">
                    Applied {a.applied_at ? new Date(a.applied_at).toLocaleDateString() : "—"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="app-detail-modal" className="max-w-3xl bg-surface border-white/10 rounded-none text-white max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-3xl font-black uppercase tracking-tighter">{selected.startup_name}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <StageBadge stage={selected.status} />
                <OutcomeBadge o={selected.outcome} />
                <span className="text-xs font-mono text-white/50">Queue #{selected.queue_position}</span>
                <span data-testid="app-detail-applied" className="text-xs font-mono text-white/50">
                  Applied {selected.applied_at ? new Date(selected.applied_at).toLocaleDateString() : "—"}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="space-y-3 text-sm">
                  <Row label="Founder" value={selected.founder_name} />
                  <Row label="Email" value={selected.email} />
                  <Row label="Phone" value={selected.phone} />
                  <Row label="Website" value={<a href={selected.website?.startsWith("http") ? selected.website : `https://${selected.website}`} target="_blank" rel="noreferrer" className="text-lime hover:underline break-all">{selected.website}</a>} />
                  <Row label="Country" value={selected.country} />
                  <Row label="Year" value={selected.year_incorporation} />
                  <Row label="Industry" value={selected.industry} />
                </div>
                <div className="space-y-3 text-sm">
                  <Row label="One-liner" value={selected.one_liner} />
                  {selected.long_description && <Row label="Description" value={<span className="whitespace-pre-wrap">{selected.long_description}</span>} />}
                  <Row label="Scheduled" value={selected.scheduled_at || "—"} />
                  <Row label="Live URL" value={selected.linkedin_live_url || "—"} />
                </div>
              </div>

              <div className="mt-6 space-y-4 border-t border-white/10 pt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Move to stage</Label>
                    <select
                      data-testid="app-stage-select"
                      value={selected.status}
                      onChange={(e) => updateApp(selected.id, { status: e.target.value })}
                      className={`mt-2 w-full h-11 px-3 ${inputCls}`}
                    >
                      {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Outcome (post-pitch)</Label>
                    <select
                      data-testid="app-outcome-select"
                      value={selected.outcome || ""}
                      onChange={(e) => updateApp(selected.id, { outcome: e.target.value || null })}
                      className={`mt-2 w-full h-11 px-3 ${inputCls}`}
                    >
                      <option value="">—</option>
                      {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Schedule (date/time)</Label>
                    <Input data-testid="app-schedule-input" type="datetime-local" defaultValue={selected.scheduled_at?.slice(0, 16) || ""} onBlur={(e) => e.target.value !== (selected.scheduled_at?.slice(0, 16) || "") && updateApp(selected.id, { scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className={`mt-2 ${inputCls}`} />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-white/50">LinkedIn URL</Label>
                    <Input data-testid="app-live-url" defaultValue={selected.linkedin_live_url || ""} onBlur={(e) => e.target.value !== (selected.linkedin_live_url || "") && updateApp(selected.id, { linkedin_live_url: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="https://linkedin.com/…" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Application date</Label>
                    <Input
                      data-testid="app-applied-input"
                      type="date"
                      defaultValue={(selected.applied_at || "").slice(0, 10)}
                      onBlur={(e) => {
                        const nextDate = e.target.value;
                        const currentDate = (selected.applied_at || "").slice(0, 10);
                        if (nextDate !== currentDate) {
                          updateApp(selected.id, { applied_at: nextDate ? new Date(nextDate + "T00:00:00Z").toISOString() : null });
                        }
                      }}
                      className={`mt-2 ${inputCls}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Private notes (due diligence)</Label>
                  <Textarea data-testid="app-notes" rows={4} defaultValue={selected.private_notes || ""} onBlur={(e) => e.target.value !== (selected.private_notes || "") && updateApp(selected.id, { private_notes: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="Diligence notes, red flags, follow-up items…" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    data-testid="publish-episode-btn"
                    onClick={() => onPublishAsEpisode(selected)}
                    className="px-6 py-3 bg-lime text-black font-heading font-bold uppercase tracking-wider hover:scale-[1.03] transition-transform"
                  >
                    Publish as episode →
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
